'use client'

import { Button } from '@/src/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/src/components/ui/form'
import { Input } from '@/src/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/src/components/ui/select'
import { Textarea } from '@/src/components/ui/textarea'
import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'

// Esquema de validación con Zod
const contactFormSchema = z.object({
  name: z.string().min(2, 'El nombre debe tener al menos 2 caracteres'),
  email: z.string().email('Ingresa un email válido'),
  subject: z.string().min(1, 'Selecciona un asunto'),
  message: z.string().min(10, 'El mensaje debe tener al menos 10 caracteres'),
})

// Tipo derivado del esquema
type ContactFormValues = z.infer<typeof contactFormSchema>

export function ContactForm() {
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false)

  // Inicializar el formulario con react-hook-form y zod
  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      name: '',
      email: '',
      subject: '',
      message: '',
    },
    mode: 'onChange', // Validar al cambiar para mostrar errores en tiempo real
  })

  // Activar la validación en tiempo real
  form.watch()

  const onSubmit = async (data: ContactFormValues) => {
    setIsSubmitDisabled(true)

    try {
      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      })

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.error || 'Error en el servidor')
      }

      if (result.error) {
        toast.error('Error', {
          description: result.error,
        })
        setIsSubmitDisabled(false)
      }
      else {
        // Mostrar mensaje de éxito y mantener el formulario con los datos
        toast.success('Mensaje enviado', {
          description: 'Gracias por contactarnos. Te responderemos a la brevedad.',
        })

        // Mantener el botón deshabilitado durante 5 segundos
        setTimeout(() => {
          setIsSubmitDisabled(false)
        }, 5000)
      }
    }
    catch (err: any) {
      toast.error('Error', {
        description: 'message' in err ? err.message as string : 'Error en el servidor',
      })
      setIsSubmitDisabled(false)
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Nombre completo *</FormLabel>
              <FormControl>
                <Input
                  placeholder="Tu nombre completo"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email *</FormLabel>
              <FormControl>
                <Input
                  type="email"
                  placeholder="tu@email.com"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="subject"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Asunto *</FormLabel>
              <Select
                onValueChange={field.onChange}
                defaultValue={field.value}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona una opción" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="consulta">Consulta general</SelectItem>
                  <SelectItem value="reserva">Reserva de mesa</SelectItem>
                  <SelectItem value="catering">Información sobre catering</SelectItem>
                  <SelectItem value="sugerencia">Sugerencia</SelectItem>
                  <SelectItem value="reclamacion">Reclamación</SelectItem>
                  <SelectItem value="otro">Otro</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="message"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                Mensaje *
                <span className="text-xs text-gray-500 ml-1">(mínimo 10 caracteres)</span>
              </FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Escribe tu mensaje aquí..."
                  rows={6}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button
          type="submit"
          variant="primary"
          className="w-full"
          disabled={form.formState.isSubmitting || isSubmitDisabled}
        >
          {form.formState.isSubmitting ? 'Enviando...' : 'Enviar mensaje'}
        </Button>
      </form>
    </Form>
  )
}
